import { useContext, useEffect, useState } from "react";
import { CommercialRegistrationReceptionBookFeedPresenter } from "./presenter";
import { useCommercialRequesterType } from "./hooks/useCommercialRequesterType";
import { useCommercialReceptionReason } from "./hooks/useCommercialReceptionReason";
import {
  type IFilterCondition,
  type ICommercialFeedFetchParams,
  type ICommercialSearchFormData,
  type CommercialReceptionBookViewPlan,
} from "./types";
import { useCommercialImportStatus } from "./hooks/useCommercialImportStatus";
import { usePrefectures } from "@/hooks/usePrefectures";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { type GridApiCommunity } from "@mui/x-data-grid-pro/internals";
import {
  getStartAndEndDate,
  getSearchMinDate,
  getSearchMaxDate,
  dateToString,
} from "./utils/dateTime";
import { isValid } from "date-fns";
import { userDataContext } from "@/hooks/useUserData";
import { useUserMe } from "@/features/login/hooks/useUserMe";
import {
  toPurchaseStatusFilter,
  toPurchaseStatusParam,
} from "./utils/purchaseStatus";

const CommercialRegistrationReceptionBookFeedContainer: React.FC = () => {
  const [startDate] = getStartAndEndDate();
  const minDate = getSearchMinDate();
  const maxDate = getSearchMaxDate();

  const { data: userData } = useContext(userDataContext);
  const { userMe } = useUserMe();
  const plan: CommercialReceptionBookViewPlan =
    userData.commercialReceptionBookViewPlan ?? "pay_per_view";

  useEffect(() => {
    userMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MUI無料版ではGridApiRefは利用できない
  const gridApiRef: React.MutableRefObject<GridApiCommunity> = useGridApiRef();

  // エラーメッセージを管理するステート変数（商業登記用に簡素化）
  const [errorMessages, setErrorMessages] = useState<{
    dateStart: string;
    dateEnd: string;
  }>({
    dateStart: "",
    dateEnd: "",
  });

  // 商業登記用のフィルター条件（将来の検索履歴機能で使用予定）
  const [_filterCondition, setFilterCondition] = useState<IFilterCondition>({
    companyNumber: "",
    companyName: "",
    companyType: [],
    receptionDateStart: startDate,
    receptionDateEnd: null,
    receptionNumber: "",
    receptionType: [],
    remarks: "",
    prefectures: [],
  });

  // 商業登記用hooks
  const { importStatuses: commercialImportStatuses } =
    useCommercialImportStatus();
  const { requesterTypeOptions } = useCommercialRequesterType();
  const { receptionReasonOptions: commercialReceptionReasonOptions } =
    useCommercialReceptionReason();
  const { prefectures } = usePrefectures();
  const prefectureOptions = prefectures.map((pref) => ({
    value: pref.id.toString(),
    label: pref.name,
  }));

  // 商業登記フィード検索用のパラメータ状態
  const [feedParams, setFeedParams] =
    useState<ICommercialFeedFetchParams | null>(null);

  /**
   * 日付のバリデーションを行う
   * @param date バリデーション対象の日付
   * @param dateName 日付の名前（エラーメッセージに使用）
   * @returns エラーメッセージ（空文字の場合エラーなし）
   */
  const validateDate = (date: Date | null, dateName: string): string => {
    const isDateMissing = date === null;
    const isDateOutOfRange =
      date !== null &&
      (date.getTime() < minDate.getTime() ||
        date.getTime() > maxDate.getTime());

    // 日付の有効性チェック
    const hasInvalidFormat = date !== null && !isValid(date);

    if (isDateMissing) {
      return `受付年月日 ${dateName}を選択して下さい`;
    }
    if (hasInvalidFormat) {
      return `受付年月日 ${dateName}を正しい日付の形式にして下さい`;
    }
    if (isDateOutOfRange) {
      return `受付年月日 ${dateName}が範囲外です`;
    }
    return "";
  };

  const handleSearch = (formData: ICommercialSearchFormData): void => {
    // エラーメッセージをリセット
    const newErrorMessages = {
      dateStart: "",
      dateEnd: "",
    };

    // 日付バリデーション
    newErrorMessages.dateStart = validateDate(
      formData.receptionDateRange[0],
      "開始"
    );
    newErrorMessages.dateEnd = validateDate(
      formData.receptionDateRange[1],
      "終了"
    );

    // エラーがあるかどうかを判定
    const hasError =
      Boolean(newErrorMessages.dateStart) || Boolean(newErrorMessages.dateEnd);

    // エラーメッセージを更新
    setErrorMessages(newErrorMessages);

    // エラーがある場合は検索処理を実行しない
    if (hasError) {
      return;
    }

    // 商業登記用のフィルター条件を更新
    const currentFilterCondition: IFilterCondition = {
      companyNumber: formData.companyNumber,
      companyName: formData.companyName,
      companyType: formData.companyType,
      receptionDateStart: formData.receptionDateRange[0],
      receptionDateEnd: formData.receptionDateRange[1],
      receptionNumber: formData.receptionNumber,
      receptionType: formData.receptionType,
      remarks: formData.remarks,
      prefectures: formData.prefectures,
    };

    setFilterCondition(currentFilterCondition);

    // 購入状況フィルタを構築（両方チェックまたは両方未チェックの場合は全件返却）
    // 定額プランでは購入状況フィルタが非表示要件のため、フォーム値が残っていても送信しない
    const purchaseStatusFilter = toPurchaseStatusFilter(
      formData.purchaseStatusPurchased,
      formData.purchaseStatusUnpurchased
    );
    const purchaseStatusParam = toPurchaseStatusParam(
      plan,
      purchaseStatusFilter
    );

    // 商業登記フィード用の検索パラメータを構築
    const newFeedParams: ICommercialFeedFetchParams = {
      companyNumber: formData.companyNumber || undefined,
      companyName: formData.companyName || undefined,
      companyType:
        formData.companyType.length > 0
          ? formData.companyType.map((v) => Number(v))
          : undefined,
      receptionDateRange: {
        start: dateToString(formData.receptionDateRange[0]),
        end: dateToString(formData.receptionDateRange[1]),
      },
      receptionNumber: formData.receptionNumber || undefined,
      receptionType:
        formData.receptionType.length > 0
          ? formData.receptionType.map((v) => Number(v))
          : undefined,
      remarks: formData.remarks || undefined,
      prefectures:
        formData.prefectures.length > 0
          ? formData.prefectures.map((v) => Number(v))
          : undefined,
      purchaseStatus: purchaseStatusParam,
    };

    // 商業登記フィードAPIをリクエスト
    setFeedParams(newFeedParams);
  };

  return (
    <CommercialRegistrationReceptionBookFeedPresenter
      gridApiRef={gridApiRef}
      handleSearch={handleSearch}
      requesterTypeOptions={requesterTypeOptions}
      commercialReceptionReasonOptions={commercialReceptionReasonOptions}
      commercialImportStatuses={commercialImportStatuses}
      feedParams={feedParams}
      prefectureOptions={prefectureOptions}
      startDate={startDate}
      minDate={minDate}
      maxDate={maxDate}
      errorMessages={errorMessages}
      plan={plan}
    />
  );
};

export { CommercialRegistrationReceptionBookFeedContainer };
